import { LocalStorageKeys } from '@constants/enums';
import { LEGACY_LOGIN, LEGACY_LOGIN_STATUS } from '@constants/auth.constants';
import storageService from './storageService';
import {
  LegacyTokens,
  CognitoTokens,
  AuthTokens,
  LegacyAuthResponse,
  CognitoLoginResponse,
} from './types';

/**
 * Check if current auth method is legacy
 * @returns True if legacy auth is enabled
 */
export const isLegacyAuth = (): boolean =>
  LEGACY_LOGIN === LEGACY_LOGIN_STATUS.ENABLED;

/**
 * Get the current auth token from the appropriate storage
 * @returns Current auth token or null
 */
export const getAuthToken = (): string | null =>
  isLegacyAuth()
    ? localStorage.getItem(LocalStorageKeys.TOKEN)
    : storageService.getIdToken();

/**
 * Get the current refresh token from the appropriate storage
 * @returns Current refresh token or null
 */
export const getRefreshToken = (): string | null =>
  isLegacyAuth()
    ? localStorage.getItem(LocalStorageKeys.REFRESH)
    : storageService.getRefreshToken();

/**
 * Get the ID token
 * @returns ID token or null
 */
export const getIdToken = (): string | null =>
  isLegacyAuth() ? null : storageService.getIdToken();

/**
 * Get the access token
 * @returns Access token or null
 */
export const getAccessToken = (): string | null =>
  isLegacyAuth() ? null : storageService.getAccessToken();

/**
 * Clear auth data from the appropriate storage
 */
export const clearAuthData = (): void => {
  if (isLegacyAuth()) {
    // Clear legacy storage
    localStorage.removeItem(LocalStorageKeys.TOKEN);
    localStorage.removeItem(LocalStorageKeys.REFRESH);
    localStorage.removeItem(LocalStorageKeys.IS_KYC);
  } else {
    // Clear Cognito storage
    storageService.clearAuthData();
  }
};

/**
 * Get auth method name for logging/tracking
 * @returns Human readable auth method name
 */
export const getAuthMethodName = (): string =>
  isLegacyAuth() ? 'Legacy' : 'Cognito';

/**
 * Safely parse JSON with fallback value
 * @param value String to parse
 * @param fallback Fallback value if parsing fails
 * @returns Parsed value or fallback
 */
export const safeParseJSON = (value: string | null, fallback: any): any => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Clear invalid auth data from storage
 */
export const clearInvalidAuth = (): void => {
  if (isLegacyAuth()) {
    localStorage.clear();
  } else {
    storageService.clearAuthData();
  }
};

/**
 * Check if tokens are legacy tokens
 * @param tokens - Auth tokens to check
 * @returns True if tokens are legacy type
 */
export const isLegacyTokens = (tokens: AuthTokens): tokens is LegacyTokens =>
  'token' in tokens && 'refresh' in tokens && !('idToken' in tokens);

/**
 * Check if tokens are Cognito tokens
 * @param tokens - Auth tokens to check
 * @returns True if tokens are Cognito type
 */
export const isCognitoTokens = (tokens: AuthTokens): tokens is CognitoTokens =>
  'idToken' in tokens && 'accessToken' in tokens && 'refreshToken' in tokens;

/**
 * Check if auth response is legacy type
 * @param response - Auth response to check
 * @returns True if response is legacy type
 */
export const isLegacyAuthResponse = (
  response: any
): response is LegacyAuthResponse =>
  typeof response === 'object' &&
  response !== null &&
  'token' in response &&
  'refresh' in response &&
  'token_type' in response;

/**
 * Check if auth response is Cognito type
 * @param response - Auth response to check
 * @returns True if response is Cognito type
 */
export const isCognitoAuthResponse = (
  response: any
): response is CognitoLoginResponse =>
  typeof response === 'object' &&
  response !== null &&
  'idToken' in response &&
  'accessToken' in response &&
  'tokenType' in response;

export default {
  getAuthToken,
  getRefreshToken,
  getIdToken,
  getAccessToken,
  clearAuthData,
  isLegacyAuth,
  getAuthMethodName,
  safeParseJSON,
  clearInvalidAuth,
  isLegacyTokens,
  isCognitoTokens,
  isLegacyAuthResponse,
  isCognitoAuthResponse,
};
